import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import type { ConsultationMessage } from '@creanizer/types'
import { cn } from '@creanizer/ui'
import { User, Bot, Users, RefreshCw, Edit2, Check, X } from 'lucide-react'
import { useConsultationStore } from '../../../../stores/consultationStore'

interface ChatMessageProps {
  message: ConsultationMessage
  sessionId: string
  showRetryButton?: boolean
  showRegenerateButton?: boolean
  onRetry?: () => void
  onRegenerate?: () => void
}

export function ChatMessage({ 
  message, 
  sessionId,
  showRetryButton, 
  showRegenerateButton,
  onRetry,
  onRegenerate 
}: ChatMessageProps) {
  const consultationStore = useConsultationStore()
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(message.content)
  const isUser = message.role === 'user'
  const isSystem = message.role === 'system'
  const isConference = message.metadata?.personaId === 'conference'
  
  // ユーザー設定を取得
  const userSettings = consultationStore.userSettings
  const userIcon = consultationStore.userIcon
  const userName = userSettings?.enabled && userSettings?.name ? userSettings.name : 'あなた'
  
  // ペルソナモードの場合、キャラクターアイコンを取得
  // ペルソナIDが存在し、会議モードでない場合のみアイコンを取得
  let characterIcon = null
  try {
    if (message.metadata?.personaId && 
        message.metadata.personaId !== 'none' && 
        !isConference) {
      characterIcon = consultationStore.getCharacterIcon(message.metadata.personaId)
    }
  } catch (error) {
    // アイコン取得エラーは無視
  }
  
  if (isSystem) {
    // 要約メッセージかどうかを判定
    const isSummary = message.id.startsWith('summary_') || message.content.includes('=== これまでの会話の要約 ===')
    
    if (isSummary) {
      // 要約メッセージは目立つように表示
      return (
        <div className="my-4 p-4 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            <span className="text-sm font-semibold text-foreground">会話履歴の要約</span>
          </div>
          <div className="text-sm text-foreground/90 whitespace-pre-wrap">
            {message.content}
          </div>
        </div>
      )
    }
    
    // 通常のシステムメッセージ
    return (
      <div className="text-center text-xs text-muted-foreground py-2">
        {message.content}
      </div>
    )
  }
  
  // 会議モードの参加者リストから発言者のアイコンを取得
  const getParticipantIcon = (speakerName: string) => {
    if (!consultationStore.conferenceSettings?.participants) return null
    
    // 名前を正規化（読み仮名を除去、スペースを統一）
    const normalizeName = (name: string) => {
      // 括弧とその中身を除去（全角・半角両対応）
      let normalized = name.replace(/[（(][^）)]*[）)]/g, '').trim()
      // 全角スペースを半角に統一し、連続するスペースを1つに
      normalized = normalized.replace(/　/g, ' ').replace(/\s+/g, ' ')
      // スペースなしバージョンも生成
      const withoutSpace = normalized.replace(/\s/g, '')
      return { withSpace: normalized, withoutSpace }
    }
    
    const normalizedSpeaker = normalizeName(speakerName)
    
    const participant = consultationStore.conferenceSettings.participants.find(p => {
      const normalizedCharacter = normalizeName(p.characterName)
      // スペースあり・なし両方でマッチング
      return normalizedCharacter.withSpace === normalizedSpeaker.withSpace || 
             normalizedCharacter.withSpace === normalizedSpeaker.withoutSpace ||
             normalizedCharacter.withoutSpace === normalizedSpeaker.withSpace ||
             normalizedCharacter.withoutSpace === normalizedSpeaker.withoutSpace ||
             normalizedCharacter.withSpace.includes(normalizedSpeaker.withSpace) ||
             normalizedSpeaker.withSpace.includes(normalizedCharacter.withSpace)
    })
    
    if (participant) {
      return consultationStore.getCharacterIcon(participant.characterId)
    }
    return null
  }
  
  // 会議モードのメッセージをパース
  const parseConferenceMessage = (content: string) => {
    const lines = content.split('\n')
    const statements: { speaker: string; content: string; icon?: string | null }[] = []
    let currentSpeaker = ''
    let currentContent: string[] = []
    
    for (const line of lines) {
      const match = line.match(/^([^：:]+)[：:](.*)$/)
      if (match) {
        // 新しい発言者が見つかった場合、前の発言を保存
        if (currentSpeaker && currentContent.length > 0) {
          statements.push({
            speaker: currentSpeaker,
            content: currentContent.join('\n').trim(),
            icon: getParticipantIcon(currentSpeaker)
          })
        }
        currentSpeaker = match[1].trim()
        currentContent = [match[2].trim()]
      } else if (currentSpeaker) {
        // 継続行
        currentContent.push(line)
      } else {
        // 発言者がない行（ナレーションなど）
        if (line.trim()) {
          statements.push({
            speaker: '',
            content: line.trim(),
            icon: null
          })
        }
      }
    }
    
    // 最後の発言を保存
    if (currentSpeaker && currentContent.length > 0) {
      statements.push({
        speaker: currentSpeaker,
        content: currentContent.join('\n').trim(),
        icon: getParticipantIcon(currentSpeaker)
      })
    }
    
    return statements
  }
  
  // 会議モードの場合の特別な表示
  if (!isUser && isConference) {
    const statements = parseConferenceMessage(message.content)
    
    return (
      <div className="flex gap-1 sm:gap-3">
        {/* アイコン */}
        <div className="flex-shrink-0 w-10 h-10 sm:w-16 sm:h-16 rounded-full flex items-center justify-center bg-muted">
          <Users className="h-5 w-5 sm:h-8 sm:w-8" />
        </div>
        
        {/* メッセージ本体 */}
        <div className="flex flex-col max-w-[calc(100%-3rem)] sm:max-w-[70%] items-start">
          {/* ヘッダー */}
          <div className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 sm:mb-1">
            {message.metadata?.personaName || "会議モード"}
          </div>
          
          {/* 会議内容 */}
          <div className="rounded-lg px-2 py-1.5 sm:px-4 sm:py-2 bg-muted w-full">
            {isEditing ? (
              <div className="space-y-2">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full min-h-[200px] p-2 rounded text-sm sm:text-base resize-vertical bg-background text-foreground"
                  autoFocus
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => {
                      consultationStore.editMessage(sessionId, message.id, editContent)
                      setIsEditing(false)
                    }}
                    className="p-1 rounded hover:bg-muted-foreground/20"
                    title="保存"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setEditContent(message.content)
                      setIsEditing(false)
                    }}
                    className="p-1 rounded hover:bg-muted-foreground/20"
                    title="キャンセル"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {statements.map((statement, index) => (
                  <div key={index} className="border-l-2 border-muted-foreground/20 pl-2 sm:pl-3">
                    {statement.speaker && (
                      <div className="flex items-center gap-2 mb-1">
                        {statement.icon ? (
                          <img 
                            src={statement.icon} 
                            alt={statement.speaker} 
                            className="w-8 h-8 sm:w-12 sm:h-12 rounded-full object-cover border border-border sm:border-2"
                          />
                        ) : (
                          <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-muted flex items-center justify-center">
                            <User className="h-4 w-4 sm:h-6 sm:w-6" />
                          </div>
                        )}
                        <div className="font-semibold text-sm">
                          {statement.speaker}
                        </div>
                      </div>
                    )}
                    <div className={cn(
                      "prose prose-sm max-w-none break-words dark:prose-invert",
                      !statement.speaker && "italic text-muted-foreground",
                      statement.speaker && "ml-10 sm:ml-14"
                    )}>
                      <ReactMarkdown>
                        {statement.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* タイムスタンプとアクションボタン */}
          <div className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1">
            <span>
              {new Date(message.timestamp).toLocaleTimeString('ja-JP', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
            
            {/* 編集ボタン */}
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-1 px-2 py-1 rounded hover:bg-muted transition-colors"
                title="メッセージを編集"
              >
                <Edit2 className="h-3 w-3" />
                <span className="text-[10px] sm:text-xs">編集</span>
              </button>
            )}
            
            {/* 再生成ボタン */}
            {showRegenerateButton && onRegenerate && (
              <button
                onClick={onRegenerate}
                className="flex items-center gap-1 px-2 py-1 rounded hover:bg-muted transition-colors"
                title="このメッセージを再生成"
              >
                <RefreshCw className="h-3 w-3" />
                <span className="text-[10px] sm:text-xs">再生成</span>
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }
  
  // 通常モードの表示
  // メッセージ内容がない場合のフェイルセーフ
  if (!message.content) {
    return null
  }
  
  return (
    <div className={cn(
      "flex gap-1 sm:gap-3",
      isUser ? "flex-row-reverse" : "flex-row"
    )}>
      {/* アイコン */}
      <div className="flex-shrink-0">
        {isUser ? (
          userSettings?.enabled && userIcon ? (
            <img 
              src={userIcon} 
              alt={userName} 
              className="w-10 h-10 sm:w-16 sm:h-16 rounded-full object-cover border border-border sm:border-2"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          ) : (
            <div className="w-10 h-10 sm:w-16 sm:h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              <User className="h-5 w-5 sm:h-8 sm:w-8" />
            </div>
          )
        ) : characterIcon ? (
          <img 
            src={characterIcon} 
            alt={message.metadata?.personaName || 'Character'} 
            className="w-10 h-10 sm:w-16 sm:h-16 rounded-full object-cover border border-border sm:border-2"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
        ) : (
          <div className="w-10 h-10 sm:w-16 sm:h-16 rounded-full bg-muted flex items-center justify-center">
            <Bot className="h-5 w-5 sm:h-8 sm:w-8" />
          </div>
        )}
      </div>
      
      {/* メッセージ本体 */}
      <div className={cn(
        "flex flex-col max-w-[calc(100%-3rem)] sm:max-w-[70%]",
        isUser ? "items-end" : "items-start"
      )}>
        {/* ペルソナ名またはロール表示 */}
        <div className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 sm:mb-1">
          {isUser ? userName : message.metadata?.personaName || "AI"}
          {message.metadata?.emotionalTone && (
            <span className="ml-2 italic">
              [{message.metadata.emotionalTone}]
            </span>
          )}
        </div>
        
        {/* メッセージ内容 */}
        <div className={cn(
          "rounded-lg px-2 py-1.5 sm:px-4 sm:py-2",
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-muted"
        )}>
          {isEditing ? (
            <div className="space-y-2">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className={cn(
                  "w-full min-h-[100px] p-2 rounded text-sm sm:text-base resize-vertical",
                  isUser
                    ? "bg-primary-foreground/10 text-primary-foreground placeholder:text-primary-foreground/50"
                    : "bg-background text-foreground"
                )}
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => {
                    consultationStore.editMessage(sessionId, message.id, editContent)
                    setIsEditing(false)
                  }}
                  className="p-1 rounded hover:bg-primary-foreground/20"
                  title="保存"
                >
                  <Check className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    setEditContent(message.content)
                    setIsEditing(false)
                  }}
                  className="p-1 rounded hover:bg-primary-foreground/20"
                  title="キャンセル"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {/* メディア（画像/音声）がある場合は先に表示 */}
              {message.images && message.images.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {message.images.map((mediaData, index) => {
                    const isAudio = mediaData.startsWith('data:audio/')
                    return isAudio ? (
                      <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                        <audio src={mediaData} controls className="max-w-full h-8" />
                      </div>
                    ) : (
                      <img
                        key={index}
                        src={mediaData}
                        alt={`添付画像 ${index + 1}`}
                        className="max-w-full sm:max-w-[300px] rounded cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => {
                          const newTab = window.open()
                          if (newTab) {
                            newTab.document.write(`<img src="${mediaData}" style="max-width:100%; height:auto;" />`)
                          }
                        }}
                      />
                    )
                  })}
                </div>
              )}
              {/* テキストメッセージ（Markdown対応） */}
              <div className="prose prose-sm max-w-none break-words dark:prose-invert">
                <ReactMarkdown>
                  {message.content || "（空のメッセージ）"}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>
        
        {/* タイムスタンプとアクションボタン */}
        <div className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1">
          <span>
            {message.timestamp ? new Date(message.timestamp).toLocaleTimeString('ja-JP', {
              hour: '2-digit',
              minute: '2-digit'
            }) : "時刻不明"}
            {message.metadata?.confidence && (
              <span className="ml-2">
                確信度: {Math.round(message.metadata.confidence * 100)}%
              </span>
            )}
          </span>
          
          {/* 編集ボタン（システムメッセージ以外） */}
          {!isSystem && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-1 px-2 py-1 rounded hover:bg-muted transition-colors"
              title="メッセージを編集"
            >
              <Edit2 className="h-3 w-3" />
              <span className="text-[10px] sm:text-xs">編集</span>
            </button>
          )}
          
          {/* ユーザーメッセージの再送信ボタン */}
          {isUser && showRetryButton && onRetry && (
            <button
              onClick={onRetry}
              className="flex items-center gap-1 px-2 py-1 rounded hover:bg-muted transition-colors"
              title="このメッセージ以降を再生成"
            >
              <RefreshCw className="h-3 w-3" />
              <span className="text-[10px] sm:text-xs">再送信</span>
            </button>
          )}
          
          {/* AIメッセージの再生成ボタン */}
          {!isUser && !isSystem && showRegenerateButton && onRegenerate && (
            <button
              onClick={onRegenerate}
              className="flex items-center gap-1 px-2 py-1 rounded hover:bg-muted transition-colors"
              title="このメッセージを再生成"
            >
              <RefreshCw className="h-3 w-3" />
              <span className="text-[10px] sm:text-xs">再生成</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}